import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { Button, Heading } from '@mm/components';
import { Dropdown, Switch } from 'antd';
import { useChatStartersCRUDActions } from '@mm/state/actions/ChatStartersCRUDActinos';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import {
  activeChatbotIdSelector,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useDeleteChatStarter } from '../../../../features/beta/modules/ChatStarters/use-delete-chat-starter';
import styles from './PromoterHeader.module.css';
import { useDeleteAutomationTrigger } from '../../../../features/beta/modules/Automations/AutomationEdit/InputTriggers/use-delete-automation-trigger';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import TextInput from '../../../../../common/components/TextInput';

type Props = {
  promoter: PromoterModel;
  title: string;
  placeholder?: string;
  customActivateAction?: () => void;
  hideDeleteAction?: boolean;
  className?: string;
  headerContentClassName?: string;
  deleteWithPromoterCRUDAction?: boolean;
  onNameSave?: (v: string) => Promise<void>;
};

export const PromoterHeader = (props: Props) => {
  const {
    promoter,
    title,
    placeholder,
    customActivateAction,
    hideDeleteAction,
    className,
    headerContentClassName,
    deleteWithPromoterCRUDAction = false,
    onNameSave,
  } = props;
  const promoterCRUDActions = usePromoterCRUDActions();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const viewActions = useViewActions();
  const { deleteChatStarter } = useDeleteChatStarter(
    deleteWithPromoterCRUDAction,
  );

  const [isEditingName, setIsEditingName] = useState(false);
  const [stateName, setStateName] = useState(title);

  const { automationId, handleDelete } = useDeleteAutomationTrigger();

  const { retrieveAll } = useChatStartersCRUDActions();

  const onClickActivate = async () => {
    if (customActivateAction) {
      return customActivateAction();
    }
    await promoterCRUDActions.update(chatbotId, {
      id: promoter.id,
      active: !promoter.active,
    });
    await retrieveAll({
      botId: chatbotId,
      kind: promoter.type,
    });
  };

  const isShopify = useSelector(selectIsShopifyContext);

  const onClickDelete = async () => {
    if (!promoter?.id) {
      return;
    }

    if (!automationId) {
      await deleteChatStarter(promoter as any);
      await retrieveAll({
        botId: chatbotId,
        kind: promoter.type,
      });
    }

    if (automationId) {
      const name = title || promoter?.name || '';
      await handleDelete('promoter', promoter.id, name);
    }

    viewActions.hideSlideoutMenu();
  };

  const items = [
    hideDeleteAction
      ? undefined
      : {
          key: '1',
          label: (
            <div
              className={classNames(styles.menuItem, styles.menuItemDanger)}
              onClick={e => {
                e.stopPropagation();
                onClickDelete();
              }}
            >
              <DeleteOutlined />
              Delete
            </div>
          ),
        },
  ].filter(Boolean) as ItemType[];

  return (
    <div className={classNames(styles.promoterHeaderBlock, className)}>
      <div
        className={classNames(
          styles.promoterHeaderContent,
          headerContentClassName,
        )}
      >
        <div className={styles.nameContainer}>
          {!isEditingName && (
            <Heading size={3} className={styles.promoterHeaderTitle}>
              <abbr title={promoter.name}>{promoter.name}</abbr>
            </Heading>
          )}

          {isEditingName && (
            <div className={styles.nameInputContainer}>
              <TextInput
                className={styles.nameInput}
                placeholder={placeholder || title}
                value={stateName}
                onChange={e => setStateName(e.target.value)}
                maxLength={100}
              />
            </div>
          )}

          {isEditingName && (
            <>
              <Button
                disabled={
                  (!!stateName?.length &&
                    stateName?.toString().trim() ===
                      title?.toString().trim()) ||
                  !stateName.length
                }
                onClick={async () => {
                  await onNameSave?.(stateName);
                  setIsEditingName(false);
                }}
              >
                Save
              </Button>
              <Button
                onClick={() => {
                  setStateName(title);
                  setIsEditingName(false);
                }}
                color="secondary-blue"
              >
                Cancel
              </Button>
            </>
          )}

          {!isEditingName && onNameSave && (
            <EditOutlined
              className={styles.edit}
              onClick={() => setIsEditingName(true)}
            />
          )}
        </div>

        <div className={styles.promoterHeaderButtons}>
          <Switch
            checkedChildren="active"
            unCheckedChildren="paused"
            checked={promoter.active}
            onChange={onClickActivate}
            className={styles.switch}
          />

          {!isShopify && !!items?.length && (
            <Dropdown
              className={styles.promoterHeaderMenu}
              openClassName={styles.promoterHeaderMenuOpen}
              overlayClassName={styles.promoterHeaderMenuOverlay}
              menu={{ items }}
              trigger={['click']}
            >
              <MoreOutlined size={12} />
            </Dropdown>
          )}

          <CloseOutlined
            size={10}
            className={styles.closeIcon}
            onClick={viewActions.hideSlideoutMenu}
          />
        </div>
      </div>
    </div>
  );
};
